import { useState, useEffect } from 'react';
import { accountsAPI, socialSecurityAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ShieldCheck, Calendar, FileText, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const CLAIM_TYPES = [
  { value: 'retirement', label: 'Retirement Benefits' },
  { value: 'disability', label: 'Disability Benefits' },
  { value: 'survivor', label: 'Survivor Benefits' },
  { value: 'medicare', label: 'Medicare' },
  { value: 'ssi', label: 'Supplemental Security Income (SSI)' },
];

export default function SocialSecurityPage() {
  const [accounts, setAccounts] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [claimType, setClaimType] = useState('retirement');
  const [depositAccount, setDepositAccount] = useState('');
  const [ssnLastFour, setSsnLastFour] = useState('');
  const [monthlyAmount, setMonthlyAmount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [accRes, claimsRes] = await Promise.all([
        accountsAPI.list(),
        socialSecurityAPI.claims()
      ]);
      setAccounts(accRes.data.accounts);
      setClaims(claimsRes.data.claims);
      if (accRes.data.accounts.length > 0) {
        setDepositAccount(accRes.data.accounts[0]._id);
      }
    } catch (err) {
      toast.error('Failed to load data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await socialSecurityAPI.claim({
        claim_type: claimType,
        deposit_account_id: depositAccount,
        ssn_last_four: ssnLastFour,
        monthly_amount: parseFloat(monthlyAmount) || 0,
        start_date: startDate,
        notes
      });
      toast.success('Social Security claim submitted successfully!');
      setSsnLastFour(''); setMonthlyAmount(''); setStartDate(''); setNotes('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Claim submission failed');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'pending_review': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'denied': return <AlertCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return <Badge className="bg-green-50 text-green-700 border-green-200">Approved</Badge>;
      case 'pending_review': return <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200">Pending Review</Badge>;
      case 'denied': return <Badge className="bg-red-50 text-red-700 border-red-200">Denied</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="page-container">
      <h2 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk' }}>Social Security</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Benefits Summary */}
        <div className="lg:col-span-2 space-y-6">
          {/* Claim Form */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <CardTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Submit a Claim</CardTitle>
                  <p className="text-sm text-muted-foreground">Apply for Social Security benefits</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Claim Type</Label>
                  <Select value={claimType} onValueChange={setClaimType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CLAIM_TYPES.map(ct => (
                        <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>SSN (Last 4 digits)</Label>
                    <Input maxLength={4} placeholder="1234" value={ssnLastFour} onChange={e => setSsnLastFour(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Expected Monthly Amount</Label>
                    <Input type="number" step="0.01" placeholder="2,500.00" value={monthlyAmount} onChange={e => setMonthlyAmount(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Deposit Account</Label>
                  <Select value={depositAccount} onValueChange={setDepositAccount}>
                    <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                    <SelectContent>
                      {accounts.map(a => (
                        <SelectItem key={a._id} value={a._id}>{a.name} ({formatCurrency(a.available_balance)})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Preferred Start Date</Label>
                  <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Additional Notes</Label>
                  <Textarea placeholder="Any additional information..." value={notes} onChange={e => setNotes(e.target.value)} />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit Claim'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Claims History & Info */}
        <div className="space-y-6">
          <Card data-testid="ss-benefits-summary">
            <CardHeader>
              <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>Benefits Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-blue-50">
                  <p className="text-xs text-blue-600 font-medium">Full Retirement Age</p>
                  <p className="text-lg font-semibold">67 years</p>
                </div>
                <div className="p-3 rounded-lg bg-green-50" data-testid="ss-next-payment-date">
                  <p className="text-xs text-green-600 font-medium">Next Payment Date</p>
                  <p className="text-lg font-semibold">
                    <Calendar className="h-4 w-4 inline mr-1" />
                    {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-gray-50" data-testid="ss-documents-section">
                  <p className="text-xs text-gray-600 font-medium">Required Documents</p>
                  <ul className="mt-2 space-y-1">
                    <li className="text-xs flex items-center gap-1"><FileText className="h-3 w-3" /> Birth Certificate</li>
                    <li className="text-xs flex items-center gap-1"><FileText className="h-3 w-3" /> Social Security Card</li>
                    <li className="text-xs flex items-center gap-1"><FileText className="h-3 w-3" /> Government ID</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Previous Claims */}
          {claims.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>Your Claims</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {claims.map(claim => (
                    <div key={claim._id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(claim.status)}
                        <div>
                          <p className="text-sm font-medium capitalize">{claim.claim_type.replace('_', ' ')}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(claim.created_at)}</p>
                        </div>
                      </div>
                      {getStatusBadge(claim.status)}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
